import { Button, Spinner } from '@design-system';
import { Trans, useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useState } from 'react';

import { buildSignInFlow } from '../../../features/auth';
import { AuthFlow } from '../../../features/auth/types';
import { useAuth, useFlow } from '../../../providers';
import { SimpleDialog } from '../../Dialog/SimpleDialog';
import { Status } from '../Status';
import { WidgetProps } from './config';
import { ManifoldContext } from './Provider';
import { BidFormRich } from './widgets/BidFormRich';
import { Connect } from './widgets/Connect';
import { Countdown } from './widgets/Countdown';
import { Interactions } from './widgets/Interactions';

interface Props extends WidgetProps {
  ranked: boolean;
}

export const AuctionForm = ({ listingId, ranked }: Props) => {
  const { user } = useAuth();
  const { t } = useTranslation('product');
  const { startFlow } = useFlow();
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const { getListingState } = useContext(ManifoldContext);
  const listing = getListingState(listingId);

  useEffect(() => {
    if (loading && listing) {
      setLoading(false);
    }
  }, [listing, loading]);

  const getAuctionInfo = () => {
    if (!listing || !user) return null;

    const { address } = listing.handlingProviderEvent;
    if (!address) return null;

    if (ranked) {
      const userOffer = listing.offers.find(
        (offer) => offer.offerer.toLowerCase() === address,
      );
      const total = listing.marketplace.listing?.totalAvailable;
      if (!userOffer || !total) return null;
      return {
        isWinning: Number(userOffer.rank) <= total,
        rank: userOffer.rank,
        amount: userOffer.amount,
        target: listing.offers[total - 1]?.amount,
      };
    }

    const userBid = listing.bids.find(
      (bid) => bid.bidder.toLowerCase() === address,
    );
    if (!userBid) return null;
    const highestBid = listing.bids.sort(
      (a, b) => Number(b.amount) - Number(a.amount),
    )[0].amount;
    return {
      isWinning: Number(highestBid) <= Number(userBid.amount),
      amount: userBid.amount,
      target: highestBid,
    };
  };

  const auctionInfo = getAuctionInfo();

  const isFinalised = false;

  const error = listing?.transactionState.error?.reason;

  const paragraphs = t(
    ranked ? 'auction.ranked.learnMore.body' : 'auction.default.learnMore.body',
    {
      returnObjects: true,
    },
  ) as unknown as string[];

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full flex-1 flex-col">
        {!loading && !isFinalised ? (
          <Countdown
            className="flex-col font-bold"
            listingId={listingId}
            title={t(
              ranked
                ? 'auction.ranked.closingDate'
                : 'auction.default.closingDate',
            )}
          />
        ) : null}
        {!loading && isFinalised ? (
          <span className="text-center font-bold">{t('auction.ended')}</span>
        ) : null}
      </div>
      <Connect className={!user || loading ? 'hidden' : 'block'} />
      {loading ? (
        <div className="flex aspect-[1/1] w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="flex flex-col">
            {user ? (
              <>
                {error ? <p className="mb-3 text-danger-3">{error}</p> : null}
                <BidFormRich listingId={listingId} />
              </>
            ) : (
              <div className="mb-3 flex w-full flex-col items-center gap-3">
                <Button
                  fullWidth
                  label={t('loginCta')}
                  onClick={() =>
                    startFlow({
                      name: AuthFlow.SignIn,
                      steps: buildSignInFlow({ optInGeneral: false }),
                    })
                  }
                  variant="primary"
                />
              </div>
            )}
            <button
              className="hyperlink w-fit self-center text-center"
              onClick={() => {
                setShowDialog(true);
              }}
              type="button"
            >
              {t(
                ranked
                  ? 'auction.ranked.learnMore.title'
                  : 'auction.default.learnMore.title',
              )}
            </button>
          </div>
          <SimpleDialog
            onClose={() => setShowDialog(false)}
            open={showDialog}
            title={t(
              ranked
                ? 'auction.ranked.learnMore.title'
                : 'auction.default.learnMore.title',
            )}
          >
            <div className="flex flex-col gap-3">
              {paragraphs.map((paragraph, index) => (
                <p key={paragraph}>
                  <Trans
                    components={{
                      faq: (
                        <a
                          className="hyperlink"
                          href="https://staging.avantarte.com/support/shipping#will-i-be-charged-duties-and-taxes-by-the-customs-of-my-country"
                          rel="noreferrer"
                          target="_blank"
                        >
                          FAQs
                        </a>
                      ),
                    }}
                    i18nKey={
                      ranked
                        ? `auction.ranked.learnMore.body.${index}`
                        : `auction.default.learnMore.body.${index}`
                    }
                  >
                    {paragraph}
                  </Trans>
                </p>
              ))}
            </div>
          </SimpleDialog>
          <hr className="w-full border-divider/20" />
          {auctionInfo ? (
            <Status
              amount={auctionInfo.amount}
              isWinning={auctionInfo.isWinning}
              rank={auctionInfo.rank}
              target={auctionInfo.target}
            />
          ) : null}
          <Interactions data-key="connect-mp-xyz" listingId={listingId} />
        </>
      )}
    </div>
  );
};
